import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import Home from './pages/Home';
import S3Upload from './pages/S3Upload';
import Auth from './pages/Auth';
import Profile from './pages/Profile';
import HubSpotLists from './pages/HubSpotLists';
import Firewall from './pages/Firewall';
import Dashboard from './pages/Dashboard';
import Groups from './pages/Groups';
import UserProfileView from './pages/UserProfileView';
import GroupDiagram from './pages/GroupDiagram';
import PagePermissionsManager from './pages/PagePermissionsManager';
import FormsManager from './pages/FormsManager';
import AppsManager from './pages/AppsManager';
import PaymentsManager from './pages/PaymentsManager';
import EnrollmentDataManager from './pages/EnrollmentDataManager';
import ScheduleBuilder from './pages/ScheduleBuilder';
import TaxonomyManager from './pages/TaxonomyManager';
import WorkSamplesManager from './pages/WorkSamplesManager';
import MembershipModelDemo from './pages/MembershipModelDemo';
import GroupsModelDemo from './pages/GroupsModelDemo';
import MembershipModelGuide from './pages/MembershipModelGuide';
import OnboardingLanding from './pages/onboarding/OnboardingLanding';
import FamiliesLanding from './pages/onboarding/FamiliesLanding';
import InquiriesManager from './pages/InquiriesManager';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CornerNotice from './components/notifications/CornerNotice';
import { useAuth } from './contexts/AuthContext';
import './App.css';

function FormsLegacyEditRedirect() {
  const { id } = useParams();
  return <Navigate to={`/forms?edit=${encodeURIComponent(id)}`} replace />;
}

const HIDE_NAV_PATHS = new Set(['/onboarding', '/families', '/inquiries']);
/** Public pages that stay open without a Platform Access grant. */
const SHELL_OPEN_PATHS = new Set(['/', '/onboarding', '/families']);

function AppShell() {
  const { pathname } = useLocation();
  const { isAuthenticated, loading, mainPlatformAccess } = useAuth();
  const hideNav = HIDE_NAV_PATHS.has(pathname);
  const shellPending = isAuthenticated && mainPlatformAccess == null;
  const shellDenied = isAuthenticated && mainPlatformAccess === false;
  const [accessNoticeOpen, setAccessNoticeOpen] = useState(false);

  useEffect(() => {
    if (mainPlatformAccess === true) {
      setAccessNoticeOpen(false);
      return;
    }
    if (!loading && shellDenied && pathname === '/') {
      setAccessNoticeOpen(true);
    }
  }, [loading, shellDenied, pathname, mainPlatformAccess]);

  if (!loading && shellPending) {
    return (
      <div style={shellStyles.loading}>
        <div className="loading-spinner loading-spinner--sm" />
      </div>
    );
  }

  if (!loading && shellDenied && !SHELL_OPEN_PATHS.has(pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="App">
      {!hideNav && <Navigation />}
      <main style={hideNav ? undefined : { paddingTop: '80px' }}>
        <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/onboarding" element={<OnboardingLanding />} />
              <Route path="/families" element={<FamiliesLanding />} />
              <Route path="/inquiries" element={
                <ProtectedRoute resource="inquiries">
                  <InquiriesManager />
                </ProtectedRoute>
              } />
              <Route path="/s3-upload" element={
                <ProtectedRoute resource="s3-upload">
                  <S3Upload />
                </ProtectedRoute>
              } />
              <Route path="/auth" element={<Auth />} />
              <Route path="/profile" element={
                <ProtectedRoute resource="profile">
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute resource="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/groups" element={
                <ProtectedRoute resource="groups">
                  <Groups />
                </ProtectedRoute>
              } />
              <Route path="/schools" element={<Navigate to="/groups" replace />} />
              <Route path="/organizations" element={<Navigate to="/groups" replace />} />
              <Route path="/user-profiles" element={
                <ProtectedRoute resource="user-profiles">
                  <UserProfileView />
                </ProtectedRoute>
              } />
              <Route path="/group-diagram" element={
                <ProtectedRoute resource="group-diagram">
                  <GroupDiagram />
                </ProtectedRoute>
              } />
              <Route path="/controls-manager" element={<Navigate to="/enrollment-data-manager" replace />} />
              <Route path="/relationships-manager" element={<Navigate to="/enrollment-data-manager" replace />} />
              <Route path="/user-profile-view-configs" element={<Navigate to="/page-permissions" replace />} />
              <Route path="/page-permissions" element={
                <ProtectedRoute resource="page-permissions">
                  <PagePermissionsManager />
                </ProtectedRoute>
              } />
              <Route path="/apps" element={
                <ProtectedRoute resource="page-permissions">
                  <AppsManager />
                </ProtectedRoute>
              } />
              <Route path="/payments" element={
                <ProtectedRoute resource="payments">
                  <PaymentsManager />
                </ProtectedRoute>
              } />
              <Route path="/enrollment-data-manager" element={
                <ProtectedRoute resource="page-permissions">
                  <EnrollmentDataManager />
                </ProtectedRoute>
              } />
              <Route path="/scheduler" element={
                <ProtectedRoute resource="scheduler">
                  <ScheduleBuilder />
                </ProtectedRoute>
              } />
              <Route path="/schedule-builder" element={<Navigate to="/scheduler" replace />} />
              <Route path="/forms" element={
                <ProtectedRoute resource="forms">
                  <FormsManager />
                </ProtectedRoute>
              } />
              <Route path="/forms/assignments" element={
                <ProtectedRoute resource="forms">
                  <Navigate to="/forms?tab=assignments" replace />
                </ProtectedRoute>
              } />
              <Route path="/forms/new" element={
                <ProtectedRoute resource="forms">
                  <Navigate to="/forms?edit=new" replace />
                </ProtectedRoute>
              } />
              <Route path="/forms/:id" element={
                <ProtectedRoute resource="forms">
                  <FormsLegacyEditRedirect />
                </ProtectedRoute>
              } />
              <Route path="/taxonomy" element={
                <ProtectedRoute resource="taxonomy">
                  <TaxonomyManager />
                </ProtectedRoute>
              } />
              <Route path="/work-samples" element={
                <ProtectedRoute resource="work-samples">
                  <WorkSamplesManager />
                </ProtectedRoute>
              } />
              {/* Client-only sandboxes / guide for docs/anchor-and-extending-memberships.md */}
              <Route path="/membership-model-guide" element={<MembershipModelGuide />} />
              <Route path="/membership-model-demo" element={<MembershipModelDemo />} />
              <Route path="/groups-model-demo" element={<GroupsModelDemo />} />
              <Route path="/hubspot-lists" element={<HubSpotLists />} />
              
              <Route path="/firewall" element={<Firewall />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {isAuthenticated && accessNoticeOpen && shellDenied ? (
        <CornerNotice
          isOpen
          onClose={() => setAccessNoticeOpen(false)}
          title="Access denied"
          text="You were sent home. This context doesn't have Platform Access. Switch context from the top right, or contact support."
          position="top-right"
          style={{ top: 88 }}
          containerColor="linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
          textColor="#9a3412"
          dismissOnClick
          autoClose={false}
          zIndex={10050}
        />
      ) : null}
    </div>
  );
}

const shellStyles = {
  loading: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff'
  }
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppShell />
      </Router>
    </AuthProvider>
  );
}

export default App;
